import Tag from 'Components/Common/Tag/Tag';
import Message from 'Components/Message';
import Spinner from 'Components/Spinner/Spinner';
import { Content, H3, Row } from 'Constants/styles';
import React, { MouseEvent, ReactElement, useState } from 'react';
import { FunctionComponent } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import getFetchOptions from 'utils/getFetchOptions';
import handleApiError from 'utils/handleApiError';

import { useCreateMerchantContext } from '../context';
import { BlueBtn, Media, MerchantBasicInfoTable, RowP } from './styles';

const MerchantBasicInfo = (): ReactElement => {
  const Icon = FaInfoCircle as FunctionComponent;

  const { merchantId, merchant, setMerchant, merchantStatus } = useCreateMerchantContext();

  const { apiBaseUri } = useSelector((state: ReduxStateType) => ({
    apiBaseUri: state.apiBaseUri,
  }));

  const [errorMsg, setErrorMsg] = useState<string>('');

  const [isCheckoutLoading, setIsCheckoutLoading] = useState<boolean>(false);
  const [isSuspendLoading, setIsSuspendLoading] = useState<boolean>(false);
  const [checkoutEnabled, setCheckoutEnabled] = useState<boolean | null>(
    merchantStatus ? merchantStatus.isCheckoutEnabled : null,
  );
  const [accountSuspended, setAccountSuspended] = useState<boolean | null>(
    merchantStatus ? merchantStatus.isSuspended : null,
  );

  const handleAuthStatus = async (e: MouseEvent<HTMLButtonElement>, type: 'checkout' | 'suspend'): Promise<void> => {
    e.preventDefault();

    if (!apiBaseUri || !merchantId) {
      return;
    }

    const confirmed = window.confirm('Are you sure you want to continue?');
    if (!confirmed) {
      return;
    }

    let body = {};
    switch (type) {
      case 'checkout':
        setIsCheckoutLoading(true);
        body = {
          isCheckoutEnabled: !checkoutEnabled,
        };
        break;

      case 'suspend':
        setIsSuspendLoading(true);
        body = {
          isSuspended: !accountSuspended,
        };
        break;
    }

    const url = `${apiBaseUri}/merchants/${merchantId}/auth`;
    const options = await getFetchOptions('POST', JSON.stringify(body));

    fetch(url, options)
      .then(async (res) => {
        if (!res.ok) {
          await handleApiError(res);
        }
        switch (type) {
          case 'checkout':
            setCheckoutEnabled((prev) => !prev);
            toast.success(`Successfully toggled ${type} status`);
            break;

          case 'suspend':
            setAccountSuspended((prev) => {
              if (merchant) setMerchant({ ...merchant, status: prev ? 'Active' : 'Suspended' });
              return !prev;
            });
            toast.success(`Successfully toggled ${type} status`);
            break;
        }
      })
      .catch((e) => {
        switch (type) {
          case 'checkout':
            toast.error(e.message || 'Failed to toggle checkout enabled');
            break;

          case 'suspend':
            toast.error(e.message || 'Failed to toggle account suspended');
            break;
        }
      })
      .finally(() => {
        setIsSuspendLoading(false);
        setIsCheckoutLoading(false);
      });
  };

  const handleCloseMsg = () => {
    setErrorMsg('');
  };

  return (
    <MerchantBasicInfoTable>
      <Content>
        {errorMsg.length > 0 && (
          <Row>
            <RowP>
              <Message className="mt-2 mb-0" description={errorMsg} success={false} handleClose={handleCloseMsg} />
            </RowP>
          </Row>
        )}
        <Row>
          <div className="col-sm-8">
            <Media>
              {merchant && (
                <>
                  <H3 className="pt-2 pb-0">
                    {!!merchant.businessProfile.brandName
                      ? merchant.businessProfile.brandName
                      : merchant.businessProfile.businessName}
                  </H3>
                </>
              )}
            </Media>
          </div>
        </Row>

        <Row>
          <RowP>Merchant Id: #{merchantId}</RowP>
        </Row>

        {merchant && merchantStatus && (
          <div className="items">
            <ul className="item-list">
              <li className="item item-list-header">
                <div className="item-row">
                  <div className={`item-col`}>
                    <div className={`item-label`}>Status</div>
                    <div className={`item-value`}>{merchant?.status || '-'}</div>
                  </div>
                  <div className={`item-col`}>
                    <div className={`item-label`}>Type</div>
                    <div className={`item-value`}>{merchant?.type || '-'}</div>
                  </div>
                  <div className={`item-col`}>
                    <div className={`item-label`}>Readable identifier</div>
                    <div className={`item-value`}>{merchant.readableIdentifier || '-'}</div>
                  </div>
                  <div className={`item-col`}>
                    <div className={`item-label`}>Payouts</div>
                    <div className={`item-value`}>{merchantStatus.payoutStatus}</div>
                  </div>

                  {'verificationResult' in merchant && (
                    <>
                      <div className={`item-col`}>
                        <div className={`item-label`}>Verified</div>
                        {merchant.verificationResult?.status === 'Pending' &&
                        merchant.verificationResult?.details &&
                        merchant.verificationResult?.details.length ? (
                          <div className={`item-value`} style={{ paddingTop: '0px' }}>
                            <Tag
                              label={`${merchant.verificationResult?.status ?? merchant?.status}`}
                              labelIcon={Icon}
                              content={merchant.verificationResult?.details ?? [merchant?.status]}
                            />
                          </div>
                        ) : (
                          <div className={`item-value`}>
                            {merchant.verificationResult?.status === 'Verified'
                              ? 'Yes'
                              : merchant.verificationResult?.status}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  {merchant.type === 'StandardMerchant' && (
                    <div className={`item-col`}>
                      {checkoutEnabled !== null && (
                        <BlueBtn
                          onClick={(e) => handleAuthStatus(e, 'checkout')}
                          disabled={isCheckoutLoading}
                          activated={!checkoutEnabled}
                        >
                          {checkoutEnabled ? 'Block Checkout' : 'Allow Checkout'}
                          {isCheckoutLoading && <Spinner height="20px" width="20px" />}
                        </BlueBtn>
                      )}
                    </div>
                  )}
                  <div className={`item-col`}>
                    {accountSuspended !== null && (
                      <BlueBtn
                        onClick={(e) => handleAuthStatus(e, 'suspend')}
                        disabled={isSuspendLoading}
                        activated={accountSuspended}
                      >
                        {accountSuspended ? 'Resume Account' : 'Suspend Account'}
                        {isSuspendLoading && <Spinner height="20px" width="20px" />}
                      </BlueBtn>
                    )}
                  </div>
                </div>
              </li>
            </ul>
          </div>
        )}
      </Content>
    </MerchantBasicInfoTable>
  );
};

export default MerchantBasicInfo;
